import { useEffect } from "react";
import { FormHelperText, Typography } from "@mui/material";
import FormItem from "@components/FormItem/FormItem";
import CustomFormLabel from "@components/FormItem/FormItem.styles";
import StepperControls from "@components/StepperControls/StepperControls";
import UnauthorizedRedirection from "@components/UnauthorizedRedirection/UnauthorizedRedirection";
import { useCheckOwnersMatter } from "@features/vessel-call/vessel-call-status/hooks/useCheckOwnersMatter.ts";
import NotifyDropdown from "@features/vessel-call/vessel-call-status/PortServices/components/NotifyDropdown";
import BunkeringFields from "@features/vessel-call/vessel-call-status/PortServices/form/bunkering/BunkeringFields";
import DebunkeringFields from "@features/vessel-call/vessel-call-status/PortServices/form/de-bunkering/DebunkeringFields";
import DeSlopingFields from "@features/vessel-call/vessel-call-status/PortServices/form/de-sloping/DeSlopingFields";
import HSEFields from "@features/vessel-call/vessel-call-status/PortServices/form/hse/HSEFields";
import PortServiceAdditionalDocuments from "@features/vessel-call/vessel-call-status/PortServices/form/PortServiceAdditionalDocuments";
import PortServiceUploadResolver from "@features/vessel-call/vessel-call-status/PortServices/form/PortServiceUploadResolver";
import SelectPortService from "@features/vessel-call/vessel-call-status/PortServices/form/SelectPortService";
import ShipChandlingFields from "@features/vessel-call/vessel-call-status/PortServices/form/ship-chandling/ShipChandlingFields";
import ShipToShipFields from "@features/vessel-call/vessel-call-status/PortServices/form/ship-to-ship/ShipToShipFields";
import {
  BUNKERING_MAP,
  DE_SLOPING_MAP,
  DEBUNKERING_MAP,
  getDefaultNotifyRoles,
  getMissingNotifyRolesMessage,
  HSE,
  isShipChandlingLifeboatsService,
  isShipToShipService,
  SERVICE_CATEGORIES_WITH_EXTRA_FIELDS,
  WASTE_DISPOSAL_MAP,
} from "@features/vessel-call/vessel-call-status/PortServices/form/utils";
import SlopsFields from "@features/vessel-call/vessel-call-status/PortServices/form/waste-disposal/SlopsFields";
import WasteDisposalFields from "@features/vessel-call/vessel-call-status/PortServices/form/waste-disposal/WasteDisposalFields";
import { NOTIFY_LIST } from "@features/vessel-call/vessel-call-status/PortServices/preview/utils";
import {
  PORT_SERVICE_DESCRIPTION_MAX_CHARS,
  PORT_SERVICE_REMARKS_MAX_CHARS,
  type PortServicesFormData,
} from "@features/vessel-call/vessel-call-status/PortServices/schema/portServicesFormSchema";
import { FormProvider, type UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useOutletContext, useParams } from "react-router-dom";

const LANG_BASE = "vessel-call.port-services";

export type AddPortServiceOutlet = {
  formData: UseFormReturn<PortServicesFormData>;
  updateFormData: (key: string, value: unknown) => void;
  handleSubmit: () => void;
  cancel: () => void;
  resetPersistedForm: () => void;
  notifyOption: string[];
  setNotifyOption: React.Dispatch<React.SetStateAction<string[]>>;
  errors: {
    notifyOption?: string;
  };
  setErrors: React.Dispatch<React.SetStateAction<{ notifyOption?: string }>>;
  submitError?: string | null;
  isSubmitting: boolean;
};

const AddPortService = () => {
  const {
    formData,
    updateFormData,
    handleSubmit,
    cancel,
    resetPersistedForm,
    notifyOption,
    setNotifyOption,
    errors,
    setErrors,
    submitError,
    isSubmitting,
  } = useOutletContext<AddPortServiceOutlet>();
  const { id: vesselCallId } = useParams();

  const serviceCategoryValue = formData.watch("serviceCategory");
  const serviceTypeValue = formData.watch("serviceType");
  const serviceSubTypeValue = formData.watch("serviceSubType");
  const { t } = useTranslation();

  // UNMOUNT - resets form and upload store if navigating away from the form
  useEffect(
    () => () => {
      resetPersistedForm();
    },
    [],
  );

  const isHseWasteDisposal =
    serviceCategoryValue === HSE.CATEGORY && serviceTypeValue === HSE.TYPE_WASTE_DISPOSAL;

  const isSlopsDisposable = serviceTypeValue === WASTE_DISPOSAL_MAP.SLOPS;
  const isShipToShip = isShipToShipService(
    serviceCategoryValue,
    serviceTypeValue,
    serviceSubTypeValue,
  );
  const isShipChandlingLifeboats = isShipChandlingLifeboatsService(
    serviceCategoryValue,
    serviceTypeValue,
  );

  const hasHseSpecificFields =
    isHseWasteDisposal && (serviceSubTypeValue === HSE.SLUDGE || serviceSubTypeValue === HSE.SLOPS);

  //default fields rendering for HSE Services and Other Services
  const shouldRenderGenericServiceFields =
    (serviceCategoryValue === HSE.CATEGORY && !hasHseSpecificFields) ||
    (!SERVICE_CATEGORIES_WITH_EXTRA_FIELDS.includes(serviceCategoryValue) &&
      !isHseWasteDisposal &&
      !isShipToShip &&
      !isShipChandlingLifeboats);

  const handleNotifyOptionChange = (opt: string[]) => {
    setNotifyOption(opt);
    setErrors((prev) => ({
      ...prev,
      notifyOption: getMissingNotifyRolesMessage(
        {
          serviceCategory: serviceCategoryValue,
          serviceType: serviceTypeValue,
          serviceSubType: serviceSubTypeValue,
        },
        opt,
      ),
    }));
  };

  const setDefaultNotifyOption = (defaultRoles: string[]) => {
    setNotifyOption(defaultRoles);
    setErrors((prev) => ({ ...prev, notifyOption: undefined }));
  };

  useEffect(() => {
    const defaultRoles = getDefaultNotifyRoles({
      serviceCategory: serviceCategoryValue,
      serviceType: serviceTypeValue,
      serviceSubType: serviceSubTypeValue,
    });
    setDefaultNotifyOption(defaultRoles);
  }, [serviceCategoryValue, serviceTypeValue, serviceSubTypeValue]);

  const ownersMatter = useCheckOwnersMatter(vesselCallId || "");

  if (ownersMatter) {
    return <UnauthorizedRedirection />;
  }

  return (
    <div data-testid="port-services-form" className="flex flex-col gap-4">
      <Typography
        variant="h5"
        data-testid="port-services-form-title"
        sx={{ color: "text.primary" }}
      >
        {t(`${LANG_BASE}.add-service`)}
      </Typography>
      <SelectPortService
        langBase={LANG_BASE}
        serviceCategoryValue={serviceCategoryValue}
        serviceTypeValue={serviceTypeValue || ""}
      />
      {serviceCategoryValue === WASTE_DISPOSAL_MAP.CATEGORY &&
        !!serviceTypeValue &&
        (isSlopsDisposable ? (
          <SlopsFields serviceType={serviceTypeValue} langBase={LANG_BASE} />
        ) : (
          <WasteDisposalFields langBase={LANG_BASE} serviceType={serviceTypeValue} />
        ))}
      {hasHseSpecificFields && (
        <HSEFields langBase={LANG_BASE} serviceSubType={serviceSubTypeValue} />
      )}
      {serviceCategoryValue === BUNKERING_MAP.CATEGORY && <BunkeringFields langBase={LANG_BASE} />}
      {serviceCategoryValue === DEBUNKERING_MAP.CATEGORY && (
        <DebunkeringFields langBase={LANG_BASE} />
      )}
      {serviceCategoryValue === DE_SLOPING_MAP.CATEGORY && <DeSlopingFields langBase={LANG_BASE} />}
      {isShipToShip && <ShipToShipFields langBase={LANG_BASE} />}
      {isShipChandlingLifeboats && <ShipChandlingFields langBase={LANG_BASE} />}
      {shouldRenderGenericServiceFields && (
        <>
          <FormProvider {...formData}>
            <div className="grid grid-cols-2">
              <FormItem<PortServicesFormData, "rts">
                name="rts"
                type="date-picker"
                label={t(`${LANG_BASE}.fields.rts`)}
                testId="portServices-rts"
                onFieldChange={updateFormData}
                timePicker
                combinedInput
                dateConstraint="not-past"
              />
            </div>
            <FormItem<PortServicesFormData, "description">
              type="textarea"
              maxChars={PORT_SERVICE_DESCRIPTION_MAX_CHARS}
              minRows={3}
              name="description"
              label={t(`${LANG_BASE}.fields.service-description`)}
              required
              testId="portServices-description"
              fullWidth
              onFieldChange={updateFormData}
              showCharCounter
            />
          </FormProvider>
        </>
      )}

      <div className="mt-6">
        <PortServiceUploadResolver
          serviceCategory={serviceCategoryValue}
          serviceType={serviceTypeValue}
          serviceSubType={serviceSubTypeValue}
        />
      </div>

      <div className="grid grid-cols-2">
        <div className="flex flex-col gap-2">
          <CustomFormLabel data-testid="cancel-service-notify-label">
            {t(`${LANG_BASE}.notify`)}
          </CustomFormLabel>
          <NotifyDropdown
            options={NOTIFY_LIST}
            option={notifyOption}
            setOption={handleNotifyOptionChange}
            error={!!errors.notifyOption}
          />
          {errors.notifyOption && (
            <FormHelperText sx={{ color: "error.main", m: 0 }}>
              {errors.notifyOption}
            </FormHelperText>
          )}
        </div>
      </div>
      {!!serviceCategoryValue && (
        <FormProvider {...formData}>
          <FormItem<PortServicesFormData, "remarks">
            type="textarea"
            maxChars={PORT_SERVICE_REMARKS_MAX_CHARS}
            minRows={3}
            name="remarks"
            label={t(`${LANG_BASE}.fields.remarks`)}
            testId="portServices-remarks"
            fullWidth
            onFieldChange={updateFormData}
            showCharCounter
          />
        </FormProvider>
      )}
      <div>{!!serviceCategoryValue && <PortServiceAdditionalDocuments />}</div>
      <div className="py-6 border-t border-t-(--stroke-secondary-stroke,#E6EDF2)">
        {submitError && (
          <FormHelperText sx={{ color: "error.main", m: 0 }}>{submitError}</FormHelperText>
        )}
        <StepperControls
          cancel={cancel}
          complete={handleSubmit}
          completePending={isSubmitting}
          completeLabelTranslationKey={t(`${LANG_BASE}.submit-service-request`)}
        />
      </div>
    </div>
  );
};
export default AddPortService;
